import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule, FileText, Loader2, FileSpreadsheet } from 'lucide-angular';
import { LessonPlanService } from '../../../../services/lesson-plan.service';
import { AuthService } from '../../../../../../../common/auth/auth.service';
import {
  PricingModalComponent,
  PaywallTarget,
} from '../../../../../../paywall/pricing-modal/pricing-modal';
import { SubscriptionSuccessModalComponent } from '../../../../../../paywall/subscription-success-modal/subscription-success-modal';
import { Product } from '../../../../../../paywall/billing-api.service';

type PendingExportAction = 'docx' | 'googleDocs' | null;

@Component({
  selector: 'app-admin-plan-view',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, PricingModalComponent, SubscriptionSuccessModalComponent],
  templateUrl: './admin-plan-view.html',
  styleUrl: './admin-plan-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPlanViewComponent implements OnInit {
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isExportingDocx$$ = new BehaviorSubject<boolean>(false);
  protected readonly isExportingDocx$ = this.isExportingDocx$$.asObservable();

  private readonly isExportingGoogleDocs$$ = new BehaviorSubject<boolean>(false);
  protected readonly isExportingGoogleDocs$ = this.isExportingGoogleDocs$$.asObservable();

  protected readonly inputData$ = this.lessonPlanService.inputData$;
  protected readonly planStructure$ = this.lessonPlanService.planStructure$;
  protected readonly selectedObjectives$ = this.lessonPlanService.selectedObjectives$;

  // Pricing modal state
  protected readonly isPricingModalOpen = signal<boolean>(false);
  protected readonly paywallTarget = signal<PaywallTarget>('export_docx');
  private pendingExportAction: PendingExportAction = null;

  // Subscription success modal state
  protected readonly isSubscriptionSuccessOpen = signal<boolean>(false);
  protected readonly subscribedProductName = signal<string>('Premium');

  protected readonly icons = {
    FileText,
    Loader2,
    FileSpreadsheet,
  };

  ngOnInit(): void {
    // Ensure user profile is loaded
    this.authService.getProfile().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  /** Check if user has an active premium subscription */
  private hasPremium(): boolean {
    return this.authService.hasPremium();
  }

  // Pricing modal handlers
  protected openPricingModal(action: PendingExportAction): void {
    this.pendingExportAction = action;
    this.paywallTarget.set(action === 'googleDocs' ? 'export_google_docs' : 'export_docx');
    this.isPricingModalOpen.set(true);
  }

  protected closePricingModal(): void {
    this.isPricingModalOpen.set(false);
    this.pendingExportAction = null;
  }

  protected onPricingProductSelected(): void {
    // Don't close the pricing modal - let it handle showing the checkout modal internally
  }

  protected onPaymentSuccess(product: Product): void {
    this.isPricingModalOpen.set(false);
    this.subscribedProductName.set(product.name);
    this.isSubscriptionSuccessOpen.set(true);

    // Note: Profile refresh is handled by the subscription success modal's polling logic
    // Execute the pending export action after successful subscription
    this.executePendingAction();
  }

  protected closeSubscriptionSuccess(): void {
    this.isSubscriptionSuccessOpen.set(false);
  }

  private executePendingAction(): void {
    if (!this.pendingExportAction) return;

    switch (this.pendingExportAction) {
      case 'docx':
        this.executeDocxExport();
        break;
      case 'googleDocs':
        this.executeGoogleDocsExport();
        break;
    }
    this.pendingExportAction = null;
  }

  protected handleExportDocx(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // If user has premium, export directly without paywall
    if (this.hasPremium()) {
      this.executeDocxExport();
    } else {
      this.openPricingModal('docx');
    }
  }

  protected handleExportGoogleDocs(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // If user has premium, export directly without paywall
    if (this.hasPremium()) {
      this.executeGoogleDocsExport();
    } else {
      this.openPricingModal('googleDocs');
    }
  }

  private executeDocxExport(): void {
    this.isExportingDocx$$.next(true);

    this.lessonPlanService
      .exportToDocx()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isExportingDocx$$.next(false);
        },
        error: () => {
          this.isExportingDocx$$.next(false);
        },
      });
  }

  private executeGoogleDocsExport(): void {
    this.isExportingGoogleDocs$$.next(true);

    this.lessonPlanService
      .exportToGoogleDocs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isExportingGoogleDocs$$.next(false);
        },
        error: () => {
          this.isExportingGoogleDocs$$.next(false);
        },
      });
  }
}
